import React, { useEffect, useReducer, useState } from "react";
import {
  BranchModel,
  EnumAlertDialogType,
  EnumMenuType,
  EnumPopupFunctions,
  PopupInfoModel,
} from "../Managers/dataStructures";
import { filterBranchSearchData } from "../Managers/utilityFunctions";
import { getLanguage, pageIsSearchable } from "../Managers/dataProvider";
import { fetchAllBranches, timeout } from "../Managers/wepApiManager";
import { ActivityIndicator, Toast } from "../Managers/utilityComponents";

interface BranchPropsModel {
  invokeDialog: (item: PopupInfoModel) => void;
  searchString: string;
}

export default function Branch(props: BranchPropsModel) {
  const isEnglish = getLanguage();
  const [array_branches, setArray_Branches] = useState<BranchModel[]>([]);
  const [isActivityRunning, setIsActivityRunning] = useState<boolean>(false);
  const [toastText, setToastText] = useState<string>("");
  const [isToastVisible, setIsToastVisible] = useState<EnumAlertDialogType>(
    EnumAlertDialogType.NEUTRAL
  );
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    updateBranchData();
  }, []);

  const searchString: string = pageIsSearchable(EnumMenuType.BRANCHES)
    ? props.searchString
    : "";

  const isIndicatorVisible: boolean =
    isActivityRunning && array_branches.length === 0;

  function updateBranchData() {
    setIsActivityRunning(true);
    fetchAllBranches((x) => {
      x.sort((a, b) => {
        return a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1;
      });
      setArray_Branches(x);
      setIsActivityRunning(false);
    });
  }

  function addBranch() {
    const item: PopupInfoModel = {
      item: null,
      ftype: EnumPopupFunctions.ADD_BRANCH,
      callback: (item: any) => {
        const addedBranch: BranchModel = item as BranchModel;
        let tempBranches = array_branches;
        tempBranches.push(addedBranch);
        setArray_Branches(tempBranches);
      },
    };
    props.invokeDialog(item);
  }

  function handleEdit(branch: BranchModel) {
    const item: PopupInfoModel = {
      item: branch,
      ftype: EnumPopupFunctions.BRANCH_DETAIL,
      callback: () => {
        forceUpdate();
      },
    };
    props.invokeDialog(item);
  }

  async function showAlert(
    isPositive: boolean,
    text: string,
    callback: () => void
  ) {
    setToastText(text);
    if (isPositive) {
      setIsToastVisible(EnumAlertDialogType.POSITIVE);
    } else {
      setIsToastVisible(EnumAlertDialogType.NEGATIVE);
    }
    await timeout(3000);
    setIsToastVisible(EnumAlertDialogType.NEUTRAL);
    callback();
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {isEnglish ? "Branches" : "Filialen"}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {isEnglish
              ? "A list of all Branches along with their Locations."
              : "Hier finden Sie die Liste aller Filialen und ihre Adressen"}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none"></div>
        <div className="mt-4 sm:ml-4 sm:mt-0 sm:flex-none">
          <button
            onClick={addBranch}
            type="button"
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {isEnglish ? "Add Branch" : "Filiale hinzufügen"}
          </button>
        </div>
      </div>
      <div className="mt-8 flow-root">
        {isToastVisible !== EnumAlertDialogType.NEUTRAL
          ? Toast(toastText, isToastVisible)
          : null}
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="relative">
              {/* Selected row actions, only show when rows are selected. */}
              {/* <div className="absolute top-0 left-14 flex h-12 items-center space-x-3 bg-white sm:left-12"> */}
              {/*   <button type="button" className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">Bulk edit</button> */}
              {/*   <button type="button" className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">Delete all</button> */}
              {/* </div> */}

              {isIndicatorVisible ? (
                <div className="min-w-full h-20 flex flex-row items-center justify-center">
                  {ActivityIndicator(1)}
                </div>
              ) : (
                <table className="min-w-full table-fixed divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Branch Name" : "Filiale"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Address" : "Adresse"}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {isEnglish ? "Phone" : "Telefon"}
                      </th>

                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                      >
                        <span className="sr-only">
                          {isEnglish ? "Edit" : "Edit"}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-3"
                      >
                        <span className="sr-only">
                          {isEnglish ? "Delete" : "Delete"}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filterBranchSearchData(array_branches, searchString).map(
                      (x) => [CellBranch(x, handleEdit)]
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CellBranch(
  branch: BranchModel,
  handler: (branch: BranchModel) => void
) {
  const isEnglish = getLanguage();
  function onEdit() {
    handler(branch);
  }
  return (
    <tr key={branch.branch_id}>
      {/* Selected: "text-indigo-600", Not Selected: "text-gray-900" */}
      <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
        {branch.name}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {branch.address}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {branch.phone}
      </td>
      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
        <a
          onClick={onEdit}
          href={"#" + branch.branch_id}
          className="text-indigo-600 hover:text-indigo-900"
        >
          {isEnglish ? "Edit" : "Bearbeiten"}
          <span className="sr-only">{", " + branch.name}</span>
        </a>
      </td>
      {branch.isActivityRunning === true ? (
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          {ActivityIndicator(1)}
        </td>
      ) : null}
    </tr>
  );
}
